import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  OnInit,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Store } from '@ngrx/store';
import { map, Observable, shareReplay, take } from 'rxjs';
import { UserState } from '@ups/user';
import {
  purchaseOrderMatchesQuery,
  SearchVpPurchaseOrderService,
} from '@ups/tool-tracker';
import { DynamicTypeaheadWebBaseComponent } from '../base/dynamic-typeahead.web-base-component';

const SEARCH_CONTROL_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DynamicTypeaheadPurchaseOrderComponent),
  multi: true,
};

/**
 * WI 34963: Viewpoint purchase order search, scoped to the signed-in user's default company.
 * Each record carries its `vendor`, so a template can auto-fill the vendor control with
 * `updateOnValueChange: [{ formControlName: <vendor>, property: 'vendor', matchTargetProperty: 'vendor' }]`.
 */
@Component({
  selector: 'ups-dynamic-typeahead-purchase-order',
  templateUrl: 'dynamic-typeahead-purchase-order.component.html',
  providers: [SEARCH_CONTROL_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // eslint-disable-next-line @angular-eslint/prefer-standalone -- pre-commit-allow: declared in the dynamic-render NgModule with its typeahead siblings (WI 34963)
  standalone: false,
})
export class DynamicTypeaheadPurchaseOrderComponent
  extends DynamicTypeaheadWebBaseComponent
  implements OnInit
{
  private readonly store = inject(Store);
  private readonly purchaseOrderService = inject(SearchVpPurchaseOrderService);
  /** The user's default Viewpoint company, which is the PO company. */
  private readonly company$: Observable<number | null | undefined> =
    this.store.pipe(
      UserState.selectCurrentTruthy(),
      take(1),
      map((myInfo) => myInfo?.Data?.VPCompanyID),
      shareReplay(1)
    );

  override ngOnInit(): void {
    this.queryFilter = purchaseOrderMatchesQuery;
    // valueProperty is pinned to 'po' by type in createConfigGroup (dynamicValuePropertyFor).
    // Set before super.ngOnInit so its reopen prefill searches the saved PO.
    this.searchQuery = (query: unknown) =>
      this.purchaseOrderService.searchForCompany(
        `${query ?? ''}`,
        this.company$
      );
    super.ngOnInit();
    // [value]-bound template: repaint on every control write (PR 4539 thread 56562).
    this.bindViewToControl();
  }

  /**
   * The shared base writes isLoading and searchError inside the search's subscribe, which does
   * not mark an OnPush view. Mark it once each search settles so the spinner and error repaint.
   */
  override filterChangeItem(
    ...args: Parameters<DynamicTypeaheadWebBaseComponent['filterChangeItem']>
  ) {
    return super
      .filterChangeItem(...args)
      .finally(() => this.cdr?.markForCheck());
  }
}
